import { AlertTriangle, Building2, Loader2, Search, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { IEconomicGroupCompany } from '@/api/commercial/economic-groups';
import { economicGroupApi } from '@/api/commercial/economic-groups';
import { cnpjaApi } from '@/api/integrations/cnpja';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useDebounce } from '@/hooks/use-debounce';
import { isValidCNPJ } from '@/lib/formatters';

const formatCnpj = (value: string) => {
  const digits = value.replace(/\D/g, '');
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

interface EconomicGroupFormDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  initialData?: {
    id: string;
    economic_group_holding?: IEconomicGroupCompany;
    economic_group_controlled?: IEconomicGroupCompany[];
  };
  onSubmit: (data: {
    economic_group_holding?: IEconomicGroupCompany;
    economic_group_controlled?: IEconomicGroupCompany[];
  }) => void;
  isPending?: boolean;
  /** Current account's CNPJ — auto-included in controlled list when holding differs */
  accountTaxId?: string;
  /** Current account's name — used when auto-including in controlled list */
  accountName?: string;
}

export function EconomicGroupFormDialog({
  open,
  onClose,
  mode,
  initialData,
  onSubmit,
  isPending,
  accountTaxId,
  accountName,
}: EconomicGroupFormDialogProps) {
  const [holdingSearch, setHoldingSearch] = useState('');
  const [controlledSearch, setControlledSearch] = useState('');
  const [selectedHolding, setSelectedHolding] = useState<IEconomicGroupCompany | null>(null);
  const [selectedControlled, setSelectedControlled] = useState<IEconomicGroupCompany[]>([]);
  const [showHoldingDropdown, setShowHoldingDropdown] = useState(false);
  const [showControlledDropdown, setShowControlledDropdown] = useState(false);
  const [holdingSearchResults, setHoldingSearchResults] = useState<IEconomicGroupCompany[]>([]);
  const [controlledSearchResults, setControlledSearchResults] = useState<IEconomicGroupCompany[]>(
    [],
  );
  const [isSearchingHolding, setIsSearchingHolding] = useState(false);
  const [isSearchingControlled, setIsSearchingControlled] = useState(false);
  const [companiesWithExistingGroup, setCompaniesWithExistingGroup] = useState<Set<string>>(
    new Set(),
  );

  const debouncedHoldingSearch = useDebounce(holdingSearch, 500);
  const debouncedControlledSearch = useDebounce(controlledSearch, 500);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedHolding(initialData?.economic_group_holding || null);
      setSelectedControlled(initialData?.economic_group_controlled || []);
      setHoldingSearch('');
      setControlledSearch('');
      setShowHoldingDropdown(false);
      setShowControlledDropdown(false);
      setHoldingSearchResults([]);
      setControlledSearchResults([]);
      setCompaniesWithExistingGroup(new Set());
    }
  }, [open, initialData]);

  // Search CNPJ.ja for holding
  const searchCnpja = useCallback(async (value: string): Promise<IEconomicGroupCompany[]> => {
    if (!value.trim()) return [];
    const cleaned = value.replace(/\D/g, '');

    if (cleaned.length === 14 && isValidCNPJ(cleaned)) {
      const company = await cnpjaApi.findByCnpj(cleaned);
      return company ? [{ name: company.name, taxId: company.taxId }] : [];
    }

    if (value.length >= 3) {
      const results = await cnpjaApi.findByName(value);
      return results.map((r) => ({ name: r.name, taxId: r.taxId }));
    }

    return [];
  }, []);

  useEffect(() => {
    if (!open || !debouncedHoldingSearch) {
      setHoldingSearchResults([]);
      return;
    }
    let cancelled = false;
    setIsSearchingHolding(true);
    searchCnpja(debouncedHoldingSearch).then((results) => {
      if (!cancelled) {
        setHoldingSearchResults(results);
        setIsSearchingHolding(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, debouncedHoldingSearch, searchCnpja]);

  useEffect(() => {
    if (!open || !debouncedControlledSearch) {
      setControlledSearchResults([]);
      return;
    }
    let cancelled = false;
    setIsSearchingControlled(true);
    searchCnpja(debouncedControlledSearch).then((results) => {
      if (!cancelled) {
        setControlledSearchResults(results);
        setIsSearchingControlled(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, debouncedControlledSearch, searchCnpja]);

  // Filter out already selected companies
  const filteredHoldingCompanies = useMemo(() => {
    const controlledTaxIds = selectedControlled.map((c) => c.taxId);
    return holdingSearchResults.filter((c) => !controlledTaxIds.includes(c.taxId));
  }, [holdingSearchResults, selectedControlled]);

  const filteredControlledCompanies = useMemo(() => {
    const selectedTaxIds = [
      ...(selectedHolding ? [selectedHolding.taxId] : []),
      ...selectedControlled.map((c) => c.taxId),
    ];
    return controlledSearchResults.filter((c) => !selectedTaxIds.includes(c.taxId));
  }, [controlledSearchResults, selectedHolding, selectedControlled]);

  const handleSelectHolding = async (company: IEconomicGroupCompany) => {
    setSelectedHolding(company);
    setHoldingSearch('');
    setShowHoldingDropdown(false);

    // Auto-adoption: if holding already belongs to a group, adopt its controlled companies
    try {
      const result = await economicGroupApi.findByTaxId(company.taxId);
      if (result.found && result.economicGroup) {
        const adoptedControlled = result.economicGroup.economic_group_controlled || [];
        setSelectedControlled((prev) => {
          const existingTaxIds = new Set(prev.map((c) => c.taxId));
          const newCompanies = adoptedControlled.filter((c) => !existingTaxIds.has(c.taxId));
          return [...prev, ...newCompanies];
        });
      }
    } catch {
      // Silently ignore — auto-adoption is best-effort
    }

    // Auto-include: add current account to controlled list when holding differs
    if (accountTaxId && accountName && company.taxId !== accountTaxId) {
      setSelectedControlled((prev) => {
        if (prev.some((c) => c.taxId === accountTaxId)) return prev;
        return [...prev, { name: accountName, taxId: accountTaxId }];
      });
    }
  };

  const handleRemoveHolding = () => {
    setSelectedHolding(null);
  };

  const checkExistingGroup = useCallback(
    async (taxId: string) => {
      try {
        const result = await economicGroupApi.findByTaxId(taxId);
        if (result.found && result.economicGroup && result.economicGroup.id !== initialData?.id) {
          setCompaniesWithExistingGroup((prev) => new Set([...prev, taxId]));
        }
      } catch {
        // Silently ignore — check is best-effort
      }
    },
    [initialData?.id],
  );

  const handleSelectControlled = (company: IEconomicGroupCompany) => {
    setSelectedControlled((prev) => [...prev, company]);
    setControlledSearch('');
    setShowControlledDropdown(false);
    checkExistingGroup(company.taxId);
  };

  const handleRemoveControlled = (taxId: string) => {
    setSelectedControlled((prev) => prev.filter((c) => c.taxId !== taxId));
    setCompaniesWithExistingGroup((prev) => {
      const next = new Set(prev);
      next.delete(taxId);
      return next;
    });
  };

  const missingHolding = !selectedHolding;
  const hasConflictingCompanies = companiesWithExistingGroup.size > 0;

  const handleSubmit = () => {
    if (missingHolding || hasConflictingCompanies) return;
    onSubmit({
      economic_group_holding: selectedHolding || undefined,
      economic_group_controlled: selectedControlled.length > 0 ? selectedControlled : undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Novo Grupo Econômico' : 'Editar Grupo Econômico'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Holding Section */}
          <div className="space-y-3">
            <Label>Holding (Empresa Controladora) *</Label>

            {selectedHolding ? (
              <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/30">
                <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{selectedHolding.name}</p>
                  <p className="text-xs text-muted-foreground font-mono">
                    {formatCnpj(selectedHolding.taxId)}
                  </p>
                </div>
                {mode !== 'edit' && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 flex-shrink-0"
                    onClick={handleRemoveHolding}
                    disabled={isPending}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ) : mode !== 'edit' ? (
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por CNPJ ou nome..."
                    value={holdingSearch}
                    onChange={(e) => {
                      setHoldingSearch(e.target.value);
                      setShowHoldingDropdown(true);
                    }}
                    onFocus={() => setShowHoldingDropdown(true)}
                    onBlur={() => setTimeout(() => setShowHoldingDropdown(false), 200)}
                    className="pl-9"
                    disabled={isPending}
                  />
                </div>

                {showHoldingDropdown &&
                  (isSearchingHolding || filteredHoldingCompanies.length > 0) && (
                    <div className="absolute z-50 w-full mt-1 border rounded-lg bg-background shadow-lg">
                      <ScrollArea className="max-h-48">
                        {isSearchingHolding ? (
                          <div className="flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Buscando...
                          </div>
                        ) : (
                          filteredHoldingCompanies.map((company) => (
                            <button
                              key={company.taxId}
                              type="button"
                              className="w-full px-3 py-2 text-left hover:bg-accent transition-colors"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleSelectHolding(company)}
                            >
                              <p className="font-medium text-sm">{company.name}</p>
                              <p className="text-xs text-muted-foreground font-mono">
                                {formatCnpj(company.taxId)}
                              </p>
                            </button>
                          ))
                        )}
                      </ScrollArea>
                    </div>
                  )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Nenhuma holding definida</p>
            )}
          </div>

          {/* Controlled Companies Section */}
          <div className="space-y-3">
            <Label>Empresas Controladas</Label>

            {selectedControlled.length > 0 && (
              <div className="space-y-2">
                {selectedControlled.map((company) => (
                  <div key={company.taxId} className="space-y-1">
                    <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/30">
                      <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{company.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">
                          {formatCnpj(company.taxId)}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 flex-shrink-0"
                        onClick={() => handleRemoveControlled(company.taxId)}
                        disabled={isPending}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    {companiesWithExistingGroup.has(company.taxId) && (
                      <div className="flex items-center gap-1.5 px-3 text-xs text-destructive">
                        <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                        <span>
                          Esta conta já pertence a outro grupo econômico. Remova-a antes de
                          adicioná-la a esse grupo econômico.
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Adicionar empresa controlada..."
                  value={controlledSearch}
                  onChange={(e) => {
                    setControlledSearch(e.target.value);
                    setShowControlledDropdown(true);
                  }}
                  onFocus={() => setShowControlledDropdown(true)}
                  onBlur={() => setTimeout(() => setShowControlledDropdown(false), 200)}
                  className="pl-9"
                  disabled={isPending}
                />
              </div>

              {showControlledDropdown &&
                (isSearchingControlled || filteredControlledCompanies.length > 0) && (
                  <div className="absolute z-50 w-full mt-1 border rounded-lg bg-background shadow-lg">
                    <ScrollArea className="max-h-48">
                      {isSearchingControlled ? (
                        <div className="flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Buscando...
                        </div>
                      ) : (
                        filteredControlledCompanies.map((company) => (
                          <button
                            key={company.taxId}
                            type="button"
                            className="w-full px-3 py-2 text-left hover:bg-accent transition-colors"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleSelectControlled(company)}
                          >
                            <p className="font-medium text-sm">{company.name}</p>
                            <p className="text-xs text-muted-foreground font-mono">
                              {formatCnpj(company.taxId)}
                            </p>
                          </button>
                        ))
                      )}
                    </ScrollArea>
                  </div>
                )}
            </div>
          </div>
        </div>

        {missingHolding && (
          <p className="text-sm text-destructive">
            Selecione uma holding para criar o grupo econômico.
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isPending || missingHolding || hasConflictingCompanies}
          >
            {isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            {mode === 'create' ? 'Criar' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
